/**
 * OneGraph IRI (Internationalized Resource Identifier) Utility
 *
 * Converts draft IDs to OneGraph IRI format for SmartApplyApplicationState queries.
 * The IRI format is required for OneGraph's node(id:) GraphQL pattern.
 *
 * IRI Format: `iri://{hostname}/SmartApplyApplicationState/{draftId}`
 *
 * @example
 * ```typescript
 * const nodeId = getIriId({
 *   draftId: '12345',
 *   oneGraphUrl: 'https://apis.qa.indeed.net/graphql'
 * });
 * // Returns: "iri://apis.qa.indeed.net/SmartApplyApplicationState/12345"
 * ```
 */

/**
 * Creates an IRI (Internationalized Resource Identifier) for OneGraph SmartApplyApplicationState
 *
 * @param config - Configuration containing draftId and oneGraphUrl
 * @returns IRI string in format: `iri://{hostname}/SmartApplyApplicationState/{draftId}`
 *
 * @throws {Error} When draftId is missing or empty
 * @throws {Error} When oneGraphUrl is missing, empty, or invalid
 *
 * @example
 * ```typescript
 * // Basic usage
 * const nodeId = getIriId({
 *   draftId: 'draft_123',
 *   oneGraphUrl: 'https://apis.qa.indeed.net/graphql'
 * });
 *
 * // Use with GraphQL query
 * const { data } = useQuery(GET_SMART_APPLY_STATE, {
 *   variables: { nodeId }
 * });
 * ```
 */
export function getIriId(config: { draftId: string; oneGraphUrl: string }): string {
    const { draftId, oneGraphUrl } = config;

    // Trim whitespace from inputs
    const trimmedDraftId = draftId?.trim();
    const trimmedOneGraphUrl = oneGraphUrl?.trim();

    // Validate draftId
    if (!trimmedDraftId || trimmedDraftId.length === 0) {
        throw new Error('getIriId: draftId is required and cannot be empty');
    }

    // Validate oneGraphUrl
    if (!trimmedOneGraphUrl || trimmedOneGraphUrl.length === 0) {
        throw new Error('getIriId: oneGraphUrl is required and cannot be empty');
    }

    // Extract hostname from OneGraph URL
    let hostname: string;
    try {
        const url = new URL(trimmedOneGraphUrl);
        hostname = url.hostname;
    } catch {
        throw new Error(
            `getIriId: Invalid oneGraphUrl "${trimmedOneGraphUrl}". Must be a valid URL.`,
        );
    }

    if (!hostname) {
        throw new Error(
            `getIriId: Could not extract hostname from oneGraphUrl "${trimmedOneGraphUrl}"`,
        );
    }

    // Create IRI in OneGraph format
    return `iri://${hostname}/SmartApplyApplicationState/${trimmedDraftId}`;
}
